import { create } from 'zustand';

import type {
  Persona,
  PersonaChatModel,
  ResearchPollDraft,
  ResearchRoomFocus,
} from '@repo/contracts';
import { PERSONA_CHAT_MODEL_IDS } from '@repo/contracts/persona-models';

/**
 * A part of a screen the room is talking about, lit up on the canvas. Client
 * state: where the researcher is looking, not what the room said — the focus
 * itself lives on the message.
 */
export interface Spotlight {
  /** Changes on every request, so asking for the same place again replays. */
  key: string;
  captureId: string;
  focus: ResearchRoomFocus;
  /** Who is talking about it; null for the researcher's own question. */
  speaker: Pick<Persona, 'accent' | 'avatarProfile' | 'id' | 'name'> | null;
  /** Raised by the conversation, or by hovering a line, rather than by a
   *  click: lit where it is, quietly, and the board never moves for it.
   *  Moving the board on every line took it away from the researcher. */
  auto: boolean;
}

/**
 * A request to the board about the room's write-up card: glide to it, or
 * back to wherever the researcher was before. Keyed like a spotlight, so the
 * same request twice still moves the board.
 */
export interface AnalysisView {
  key: string;
  captureId: string;
  action: 'back' | 'show';
}

/** An answer in the thread to scroll to and flash — from the write-up card,
 *  which cites it. */
export interface RevealMessage {
  key: string;
  messageId: string;
}

/**
 * How a session's board came to be — the walk the moderator narrated on
 * `/browse` — so the room it hands over to keeps that thread instead of
 * starting blank. Client state: it is what this tab watched happen, never
 * stored, and gone on reload.
 */
export interface Arrival {
  sessionId: string;
  host: string;
  /** How many screens the page was read in. Absent for an extension audit,
   *  which arrives as one screenshot rather than a walk. */
  screens?: number;
}

/**
 * The research room's client state (CLAUDE.md > Frontend state) — what the
 * researcher is doing with the panel, never what the room contains. Messages
 * and the room itself are server state and live in TanStack Query.
 */
interface ResearchRoomState {
  /** The walk that just made this session's board — see Arrival. */
  arrival: Arrival | null;
  setArrival: (arrival: Arrival | null) => void;
  /** Whether the room is showing beside the board. Open by default: it is
   *  where the panel lives now, not an extra tool to go looking for. */
  open: boolean;
  /** The question being written, so a suggestion chip can fill it. */
  draft: string;
  /** Whose profile card is showing, if anyone's. */
  profileId: string | null;
  /** Whether the panel manager (add / suggest / take off) is open. */
  managing: boolean;
  /**
   * A question Floto wrote (a follow-up chip, a rewrite), as it was put in
   * the composer. Sent unchanged, it skips the goal check — Floto checking
   * its own question is only a wait. Any edit makes it the researcher's
   * question again, and the check applies.
   */
  vetted: string | null;
  /** A poll to open in the composer's editor — set by "Edit poll" on a
   *  card, taken (and cleared) by the composer. */
  seededPoll: ResearchPollDraft | null;
  spotlight: Spotlight | null;
  /** A line being hovered in the thread: its place, lit for as long as the
   *  pointer stays. Drawn over `spotlight` while it lasts. */
  previewSpot: Spotlight | null;
  /** Ask with everyone answering at once, none hearing the others. Stays
   *  on until turned off, so a researcher can run a few rounds that way. */
  independent: boolean;
  /** Admins only: the model personas answer with, or null for the default.
   *  An internal switch for comparing models before launch. */
  personaModel: PersonaChatModel | null;
  /** Whether the canvas shows where the room's attention landed. */
  heatmap: boolean;
  /** A re-test room created and waiting for its screen to open before its
   *  replay starts — the replay has to run in that room's panel. */
  pendingReplay: string | null;
  /** The latest request about the write-up card, for the board to act on. */
  analysisView: AnalysisView | null;
  /** Whether the board is showing the write-up card the toggle took it to. */
  viewingAnalysis: boolean;
  /** Per room, the write-up last looked at (its `updatedAt`) and the themes
   *  it had — what makes the toggle's dot, and a theme's "New". */
  seenAnalysis: Record<string, { themeIds: string[]; updatedAt: string }>;
  revealMessage: RevealMessage | null;
  /** Where each screen's fix list card sits on the board, in board pixels —
   *  its own object, like a Figma layer: moving the screen leaves it where
   *  it is. Absent until it is moved (or its screen is), when it sits beside
   *  its screen. Kept in this browser between visits. */
  fixCardPositions: Record<string, { x: number; y: number }>;
  /** Changes each time a round this tab asked finishes — what writes a
   *  milestone up, in the tab that asked and no other. */
  askedRoundEnded: string | null;
  setOpen: (open: boolean) => void;
  setDraft: (draft: string) => void;
  showProfile: (profileId: string | null) => void;
  setManaging: (managing: boolean) => void;
  /** Fills the composer with a question Floto wrote. */
  suggest: (question: string) => void;
  /** Puts a question and its poll in the composer, ready to edit. */
  editPoll: (text: string, poll: ResearchPollDraft) => void;
  takeSeededPoll: () => ResearchPollDraft | null;
  showSpotlight: (spotlight: Omit<Spotlight, 'key'>) => void;
  clearSpotlight: () => void;
  previewSpotlight: (spotlight: Omit<Spotlight, 'key'> | null) => void;
  setIndependent: (independent: boolean) => void;
  setPersonaModel: (model: PersonaChatModel | null) => void;
  setHeatmap: (heatmap: boolean) => void;
  setPendingReplay: (roomId: string | null) => void;
  /** Takes the board to the write-up card, or back from it. */
  viewAnalysis: (captureId: string, action: 'back' | 'show') => void;
  /** Fix list cards put away with their ×, by capture. The top bar's "Fix
   *  list" brings one back. */
  closedFixCards: Record<string, true>;
  closeFixCard: (captureId: string) => void;
  /** Leaving the card some other way — panning off it — ends viewing. */
  setViewingAnalysis: (viewing: boolean) => void;
  markAnalysisSeen: (
    roomId: string,
    seen: { themeIds: string[]; updatedAt: string },
  ) => void;
  showMessage: (messageId: string) => void;
  moveFixCard: (
    captureId: string,
    position: { x: number; y: number } | null,
  ) => void;
  /** Saves where the cards are, once a drag is done. */
  keepFixCardPositions: () => void;
  endAskedRound: () => void;
}

const initial = {
  analysisView: null,
  closedFixCards: {},
  askedRoundEnded: null,
  arrival: null,
  draft: '',
  fixCardPositions: {},
  managing: false,
  open: true,
  profileId: null,
  heatmap: false,
  independent: false,
  pendingReplay: null,
  revealMessage: null,
  seededPoll: null,
  previewSpot: null,
  seenAnalysis: {},
  spotlight: null,
  vetted: null,
  viewingAnalysis: false,
};

const FIX_CARD_POSITIONS_KEY = 'floto.fixCardPositions';
const PERSONA_MODEL_KEY = 'floto.personaModel';

/** The model picked on an earlier visit, so a tester comparing models does
 *  not re-pick after every reload. Anything not on the list reads as none. */
function storedPersonaModel(): PersonaChatModel | null {
  try {
    const raw = localStorage.getItem(PERSONA_MODEL_KEY);
    return PERSONA_CHAT_MODEL_IDS.find((id) => id === raw) ?? null;
  } catch {
    return null;
  }
}

/** Card positions from an earlier visit. Storage can be unavailable (private
 *  mode) or hold junk; either way the cards sit in their default place. */
function storedFixCardPositions(): Record<string, { x: number; y: number }> {
  try {
    const raw = localStorage.getItem(FIX_CARD_POSITIONS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, { x: number; y: number }>)
      : {};
  } catch {
    return {};
  }
}

export const useResearchRoomStore = create<ResearchRoomState>()((set, get) => ({
  ...initial,
  fixCardPositions: storedFixCardPositions(),
  personaModel: storedPersonaModel(),
  moveFixCard: (captureId, position) => {
    const next = { ...get().fixCardPositions };
    if (position) next[captureId] = position;
    else delete next[captureId];
    set({ fixCardPositions: next });
  },
  keepFixCardPositions: () => {
    try {
      // Bounded, so a long-lived browser does not grow this forever.
      const kept = Object.fromEntries(
        Object.entries(get().fixCardPositions).slice(-200),
      );
      localStorage.setItem(FIX_CARD_POSITIONS_KEY, JSON.stringify(kept));
    } catch {
      // Nothing to do — the card goes back beside its screen next visit.
    }
  },
  setArrival: (arrival) => {
    set({ arrival });
  },
  setPendingReplay: (pendingReplay) => {
    set({ pendingReplay });
  },
  closeFixCard: (captureId) => {
    set({ closedFixCards: { ...get().closedFixCards, [captureId]: true } });
  },
  viewAnalysis: (captureId, action) => {
    if (action === 'show' && get().closedFixCards[captureId]) {
      const { [captureId]: _reopened, ...rest } = get().closedFixCards;
      set({ closedFixCards: rest });
    }
    set({
      analysisView: {
        action,
        captureId,
        key: `${String(Date.now())}-${action}`,
      },
      viewingAnalysis: action === 'show',
    });
  },
  setViewingAnalysis: (viewingAnalysis) => {
    set({ viewingAnalysis });
  },
  markAnalysisSeen: (roomId, seen) => {
    set({ seenAnalysis: { ...get().seenAnalysis, [roomId]: seen } });
  },
  endAskedRound: () => {
    set({ askedRoundEnded: String(Date.now()) });
  },
  showMessage: (messageId) => {
    set({
      revealMessage: { key: `${String(Date.now())}-${messageId}`, messageId },
    });
  },
  setHeatmap: (heatmap) => {
    set({ heatmap });
  },
  setPersonaModel: (personaModel) => {
    set({ personaModel });
    try {
      if (personaModel) localStorage.setItem(PERSONA_MODEL_KEY, personaModel);
      else localStorage.removeItem(PERSONA_MODEL_KEY);
    } catch {
      // Not remembered past this page — the choice still holds until reload.
    }
  },
  setIndependent: (independent) => {
    set({ independent });
  },
  clearSpotlight: () => {
    set({ spotlight: null });
  },
  previewSpotlight: (spotlight) => {
    set({
      previewSpot: spotlight
        ? {
            ...spotlight,
            // The same place hovered again is the same preview — no replay.
            key: `preview-${spotlight.focus.label}-${String(spotlight.focus.x)}-${String(spotlight.focus.y)}`,
          }
        : null,
    });
  },
  showSpotlight: (spotlight) => {
    set({
      spotlight: {
        ...spotlight,
        key: `${String(Date.now())}-${spotlight.focus.label}`,
      },
    });
  },
  editPoll: (text, poll) => {
    set({ draft: text, seededPoll: poll, vetted: null });
  },
  takeSeededPoll: () => {
    const seeded = get().seededPoll;
    if (seeded) set({ seededPoll: null });
    return seeded;
  },
  setDraft: (draft) => {
    set({ draft });
  },
  setManaging: (managing) => {
    set({ managing });
  },
  setOpen: (open) => {
    set({ open });
  },
  showProfile: (profileId) => {
    set({ profileId });
  },
  suggest: (question) => {
    set({ draft: question, vetted: question });
  },
}));

/** For tests: the store is a singleton and leaks between them. */
export function resetResearchRoomStore(): void {
  useResearchRoomStore.setState({ ...initial, personaModel: null });
}
